<template>
  <div class="charts">
    <div class="charts-head">
      <div class="charts-title">
        <v-icon small class="mr-1" :color="brandColor">bar_chart</v-icon>
        Gráficos
      </div>
      <div class="charts-modes">
        <button
          v-for="opt in scopes"
          :key="opt.key"
          type="button"
          class="charts-mode"
          :class="{ active: scope === opt.key }"
          :style="scope === opt.key ? { background: brandColor, borderColor: brandColor } : {}"
          @click="setScope(opt.key)"
        >{{ opt.label }}</button>
      </div>
    </div>

    <v-layout row wrap>
      <v-flex xs12 md6 class="pa-2">
        <div class="chart-card">
          <div class="chart-card-title">Faturamento</div>
          <apexchart type="bar" height="260" :options="billingOptions" :series="billingSeries" />
        </div>
      </v-flex>
      <v-flex xs12 md6 class="pa-2">
        <div class="chart-card">
          <div class="chart-card-title">Ticket Médio</div>
          <apexchart type="bar" height="260" :options="ticketOptions" :series="ticketSeries" />
        </div>
      </v-flex>
      <v-flex xs12 md6 class="pa-2">
        <div class="chart-card">
          <div class="chart-card-title">Faturamento por Operação</div>
          <apexchart v-if="hasData(operationSeries)" type="donut" height="260" :options="operationOptions" :series="operationSeries" />
          <div v-else class="chart-empty">Sem dados no período</div>
        </div>
      </v-flex>
      <v-flex xs12 md6 class="pa-2">
        <div class="chart-card">
          <div class="chart-card-title">Faturamento por Bandeira</div>
          <apexchart v-if="hasData(brandSeries)" type="donut" height="260" :options="brandOptions" :series="brandSeries" />
          <div v-else class="chart-empty">Sem dados no período</div>
        </div>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";

const PIE_PALETTE = ["#4C6FFF", "#33C27F", "#FFB020", "#FF6B6B", "#9B6BFF", "#22B8CF", "#F368E0"];

export default {
  name: "Charts",
  created() {
    this.loadAll();
  },
  computed: {
    ...mapGetters({
      billingData: "dashboard/billingChart",
      ticketData: "dashboard/averageTicket",
      operationData: "dashboard/paymentKind",
      brandData: "dashboard/brandChart",
      color: "account/color",
    }),
    brandColor() {
      return this.color && String(this.color).startsWith("#") ? this.color : "#1976D2";
    },
    billingSeries() {
      return [{ name: "Faturamento", data: this.barData(this.billingData) }];
    },
    ticketSeries() {
      return [{ name: "Ticket médio", data: this.barData(this.ticketData) }];
    },
    operationSeries() {
      return this.pieData(this.operationData);
    },
    brandSeries() {
      return this.pieData(this.brandData);
    },
    billingOptions() {
      return this.barOptions(this.labelsOf(this.billingData));
    },
    ticketOptions() {
      return this.barOptions(this.labelsOf(this.ticketData));
    },
    operationOptions() {
      return this.donutOptions(this.labelsOf(this.operationData));
    },
    brandOptions() {
      return this.donutOptions(this.labelsOf(this.brandData));
    },
  },
  methods: {
    hasData(series) {
      return Array.isArray(series) && series.some((v) => Number(v) > 0);
    },
    labelsOf(chart) {
      return (chart && chart.labels) || [];
    },
    barData(chart) {
      const s = chart && chart.series;
      if (!s || !s.length) return [];
      return Array.isArray(s[0]) ? s[0].map(Number) : s.map(Number);
    },
    pieData(chart) {
      const s = (chart && chart.series) || [];
      return s.map(Number);
    },
    barOptions(categories) {
      return {
        chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit", parentHeightOffset: 0 },
        plotOptions: { bar: { borderRadius: 6, columnWidth: "45%" } },
        dataLabels: { enabled: false },
        colors: [this.brandColor],
        grid: { borderColor: "#eef1f6", strokeDashArray: 4 },
        xaxis: { categories, axisBorder: { show: false }, axisTicks: { show: false } },
        yaxis: { labels: { formatter: (v) => this.money(v) } },
        tooltip: { y: { formatter: (v) => this.money(v) } },
      };
    },
    donutOptions(labels) {
      return {
        chart: { type: "donut", fontFamily: "inherit" },
        labels,
        colors: PIE_PALETTE,
        legend: { position: "bottom" },
        dataLabels: { enabled: true, formatter: (val) => Math.round(val) + "%" },
        stroke: { width: 2 },
        plotOptions: { pie: { donut: { size: "62%" } } },
        tooltip: { y: { formatter: (v) => this.money(v) } },
      };
    },
    money(v) {
      return this.$n(Number(v) || 0, "currency");
    },
    loadAll() {
      this.$store.dispatch("dashboard/loadBillingChart");
      this.$store.dispatch("dashboard/loadTicketChart");
      this.$store.dispatch("dashboard/loadKindChart");
      this.$store.dispatch("dashboard/loadBrandChart");
    },
    setScope(key) {
      this.scope = key;
      this.$store.commit("dashboard/CHART_SCOPE", key);
      const unit = { week: "week", month: "month", year: "year" }[key];
      this.$store.commit("dashboard/CHARTS_BEGIN_DATE", moment().startOf(unit).format("YYYY-M-D").toString());
      this.$store.commit("dashboard/CHARTS_END_DATE", moment().endOf(unit).format("YYYY-M-D").toString());
      this.$store.commit("dashboard/UPDATE_CHARTS", true);
      this.loadAll();
    },
  },
  data() {
    return {
      scope: "week",
      scopes: [
        { key: "week", label: "Semana" },
        { key: "month", label: "Mês" },
        { key: "year", label: "Ano" },
      ],
    };
  },
};
</script>

<style scoped>
.charts {
  margin-top: 6px;
}
.charts >>> .flex {
  min-width: 0;
}
.charts-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 10px 12px;
}
.charts-title {
  font-size: 16px;
  font-weight: 700;
  color: #1f2733;
  display: inline-flex;
  align-items: center;
}
.charts-modes {
  display: flex;
  gap: 8px;
}
.charts-mode {
  padding: 7px 16px;
  border: 1.5px solid #e2e6ee;
  border-radius: 20px;
  background: #f4f6f9;
  color: #6b7688;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.charts-mode.active {
  color: #ffffff;
}
.chart-card {
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 8px 28px rgba(20, 30, 60, 0.08);
  padding: 18px 16px 8px;
  height: 100%;
  overflow: hidden;
}
.chart-card-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2733;
  margin-bottom: 6px;
  padding-left: 4px;
}
.chart-empty {
  height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #aab2c0;
  font-size: 14px;
}
</style>
